import {
  IMultipleRowActions,
  Modal,
  Table,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Chip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  IInvoiceServiceAgyLogData,
} from "../../../../models/invoice-service-agy-log";
import {
  consistInvoiceServiceAgyRetries,
  restartInvoiceServiceAgyRetries,
} from "../../../../services/invoiceServiceAgy.service";
import MainApi from "../../../../services/mainApi.service";
import InvoiceServiceAgyDetailsModal from "./InvoiceServiceAgyDetailsModal";
import MacroFilter, { IFilterParams } from "./MacroFilter";

const INVOICE_SERVICE_AGY_ROUTE =
  process.env.REACT_APP_EXTERNAL +
  "/api/v1/InvoiceServiceAgy/InvoiceServiceAgys";

const invoiceServiceAgyLogService = new MainApi<IInvoiceServiceAgyLogData>(
  INVOICE_SERVICE_AGY_ROUTE,
);

const translationPath = "page.4uBus.logs.invoiceServiceAgy.";

const InvoiceServiceAgy = () => {
  const [refresh, setRefresh] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] =
    useState<IInvoiceServiceAgyLogData | null>(null);

  const today = useMemo(() => new Date(), []);

  const { setMessage } = useNotificationContext();

  const [filterParams, setFilterParams] = useState<IFilterParams>({
    InvoiceNumber: "",
    RecCompanyName: "",
    RecCompanyDocument: "",
    IntegrationSituation: "",
    CreatedStart: today,
    CreatedEnd: today,
  });

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { sendRequest: restartInvoiceServiceAgyRetriesReq } = useFetch(
    restartInvoiceServiceAgyRetries,
  );
  const { sendRequest: consistInvoiceServiceAgyRetriesReq } = useFetch(
    consistInvoiceServiceAgyRetries,
  );

  const getStatusConfig = useCallback((statusRaw?: string | null) => {
    const status = (statusRaw || "").toUpperCase();

    const configs: Record<
      string,
      { color: "warning" | "info" | "success" | "error"; key: string }
    > = {
      "COM INCONSISTENCIA": { color: "warning", key: "with_inconsistency" },
      "AGUARDANDO ENVIO": { color: "info", key: "awaiting_send" },
      ENVIADO: { color: "success", key: "sent" },
      ERRO: { color: "error", key: "error" },
      SUCESSO: { color: "success", key: "success" },
    };

    return configs[status] || { color: "warning" as const, key: "awaiting_send" };
  }, []);

  const getRowStatus = useCallback(
    (row: IInvoiceServiceAgyLogData) => row.statusItem || row.status || "",
    [],
  );

  const normalizeStatus = useCallback((status: string) => {
    return status
      .toUpperCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }, []);

  const canShowResync = useCallback((row: IInvoiceServiceAgyLogData) => {
    const normalizedStatus = normalizeStatus(getRowStatus(row));

    return normalizedStatus === "ERRO" || normalizedStatus === "COM INCONSISTENCIA";
  }, [getRowStatus, normalizeStatus]);

  const getReceiverName = useCallback(
    (row: IInvoiceServiceAgyLogData) =>
      row.recCompanyName || row.recIndividualName || "-",
    [],
  );

  const getReceiverDocument = useCallback(
    (row: IInvoiceServiceAgyLogData) =>
      row.recCompanyDocument || row.recIndividualDocument || "-",
    [],
  );

  const getIntegrationSituation = useCallback(
    (row: IInvoiceServiceAgyLogData) =>
      row.integrationSituation || row.INTEGRATION_SITUATION || "",
    [],
  );

  const columns: GridColDef<IInvoiceServiceAgyLogData>[] = [
    {
      field: "identifier",
      headerName: t(translationPath + "identifier") || "Identificador",
      flex: 0.7,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "invoiceNumber",
      headerName: t(translationPath + "invoice_number") || "Número da Nota Fiscal",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "recCompanyName",
      headerName: t(translationPath + "rec_company_name") || "Tomador",
      flex: 1.2,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => getReceiverName(params.row),
    },
    {
      field: "recCompanyDocument",
      headerName: t(translationPath + "rec_company_document") || "CNPJ do Tomador",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => getReceiverDocument(params.row),
    },
    {
      field: "statusItem",
      headerName: t(translationPath + "status_item") || "Status",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const rawStatus = getRowStatus(params.row);
        if (!rawStatus) return "-";

        const config = getStatusConfig(rawStatus);

        return (
          <Chip
            label={t(translationPath + `status.${config.key}`)}
            color={config.color}
            size="small"
          />
        );
      },
    },
    {
      field: "integrationSituation",
      headerName:
        t(translationPath + "integration_situation") || "Status Intergação",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => getIntegrationSituation(params.row) || "-",
    },
    {
      field: "created",
      headerName: t(translationPath + "created") || "Data de Criação",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";

        const date = new Date(params.value);
        if (isNaN(date.getTime())) return "-";

        return format(date, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
    {
      field: "integratedDate",
      headerName: t(translationPath + "integrated_date") || "Data de Integração",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";

        const dateStr = params.value.toString();
        if (
          dateStr.startsWith("0001-01-01") ||
          dateStr === "0001-01-01T00:00:00"
        ) {
          return "-";
        }

        const date = new Date(params.value);
        if (isNaN(date.getTime()) || date.getFullYear() === 1) {
          return "-";
        }

        return format(date, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
    {
      field: "retries",
      headerName: t(translationPath + "retries") || "Tentativas",
      flex: 0.6,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => params.value ?? 0,
    },
    {
      field: "lastModified",
      headerName: t(translationPath + "last_modified") || "Última Modificação",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";

        const date = new Date(params.value);
        if (isNaN(date.getTime())) return "-";

        return format(date, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
  ];

  const onRefreshHandler = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      setRefresh((prev) => prev + 1);
    },
    [],
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedRow(null);
  }, []);

  const handleProcessInvoiceServiceAgyByStatus = useCallback(
    async (row: IInvoiceServiceAgyLogData) => {
      const normalizedStatus = normalizeStatus(getRowStatus(row));
      const isInconsistency = normalizedStatus === "COM INCONSISTENCIA";

      const request = isInconsistency
        ? consistInvoiceServiceAgyRetriesReq
        : restartInvoiceServiceAgyRetriesReq;

      const { success } = await request({
        identifier: row.identifier,
      });

      if (success) {
        onRefreshHandler();
        setMessage({
          message: t(`${translationPath}resetedSuccess`),
          type: "success",
        });
      }
    },
    [
      consistInvoiceServiceAgyRetriesReq,
      getRowStatus,
      normalizeStatus,
      onRefreshHandler,
      restartInvoiceServiceAgyRetriesReq,
      setMessage,
      t,
    ],
  );

  const multipleRowsActions = useMemo<IMultipleRowActions[]>(
    () => [
      {
        action: async (ids) => {
          const identifiers = ids
            .map((id) => Number(id))
            .filter((id) => !Number.isNaN(id));

          if (identifiers.length === 0) return;

          const { success } = await restartInvoiceServiceAgyRetriesReq({
            identifierList: identifiers,
          });

          if (success) {
            onRefreshHandler();
            setMessage({
              message: t(`${translationPath}resetedSuccess`),
              type: "success",
            });
          }
        },
        icon: <ReplayIcon fontSize="small" />,
        text: t(translationPath + "resync") || "Forçar reenvio",
      },
    ],
    [onRefreshHandler, restartInvoiceServiceAgyRetriesReq, setMessage, t],
  );

  return (
    <>
      <Modal open={modalOpen} onClose={handleCloseModal} maxDialogWidth="lg">
        {selectedRow && (
          <InvoiceServiceAgyDetailsModal
            invoiceServiceAgyData={selectedRow}
            onClose={handleCloseModal}
          />
        )}
      </Modal>

      <Table<IInvoiceServiceAgyLogData>
        title={t("components.menu.4ubus.logs.invoiceServiceAgy")}
        columns={columns}
        service={invoiceServiceAgyLogService}
        searchInputPlaceHolder={
          t(translationPath + "search_placeholder") || "Search..."
        }
        showDefaultMacroFilters={false}
        showButton={false}
        showDeleteButton={false}
        defaultActions={true}
        multipleRowsActions={multipleRowsActions}
        getRowId={(row) => row.identifier}
        rowActions={(params) => [
          {
            action: () => {
              setSelectedRow(params.row);
              setModalOpen(true);
            },
            icon: <VisibilityIcon fontSize="small" />,
            text: t(translationPath + "view_details") || "Ver detalhes",
          },
          canShowResync(params.row)
            ? {
                action: () => {
                  handleProcessInvoiceServiceAgyByStatus(params.row);
                },
                icon: <ReplayIcon fontSize="small" />,
                text: t(translationPath + "resync") || "Forçar reenvio",
              }
            : null,
        ]}
        serviceProps={useMemo(
          () => ({
            refresh,
            ExtensionCode: "72",
            ...filterParams,
            INTEGRATION_SITUATION:
              filterParams.IntegrationSituation || undefined,
            CreatedStart: filterParams?.CreatedStart
              ? new Date(filterParams.CreatedStart.setHours(15))
                  .toISOString()
                  .substring(0, 10)
              : undefined,
            CreatedEnd: filterParams?.CreatedEnd
              ? new Date(filterParams.CreatedEnd.setHours(15))
                  .toISOString()
                  .substring(0, 10)
              : undefined,
          }),
          [refresh, filterParams],
        )}
        macroFilters={useCallback(
          () => (
            <MacroFilter
              filterParams={filterParams}
              onFilterParams={handleFilterChange}
            />
          ),
          [filterParams, handleFilterChange],
        )}
      />
    </>
  );
};

export default InvoiceServiceAgy;

